import { Check, FileSpreadsheet, Images, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDateTime, formatNumber } from '@/lib/format-es';
import { cn } from '@/lib/utils';
import { CAPTURE_DATA_MAX, CAPTURE_PHOTO_BLOCK_SIZE } from '../captureExportModel';
import { photoBlockCount, photoBlockRows, type PreparedCaptureExport } from '../captureExportPack';

export type ExportBusy =
    | { kind: 'rows'; loaded: number; total: number }
    | { kind: 'data' }
    | { kind: 'block'; block: number; done: number; total: number };

function blockRange(block: number, totalRows: number): string {
    const from = (block - 1) * CAPTURE_PHOTO_BLOCK_SIZE + 1;
    const to = Math.min(block * CAPTURE_PHOTO_BLOCK_SIZE, totalRows);
    return `${formatNumber(from)}–${formatNumber(to)}`;
}

export function CaptureExportPanel({
    prepared,
    busy,
    doneBlocks,
    onDownloadData,
    onDownloadBlock,
    onDiscard,
}: {
    prepared: PreparedCaptureExport;
    busy: ExportBusy | null;
    doneBlocks: ReadonlySet<number>;
    onDownloadData: () => void;
    onDownloadBlock: (block: number) => void;
    onDiscard: () => void;
}) {
    const blocks = photoBlockCount(prepared.rows);
    const privateCount = prepared.rows.filter((row) => !row.isPublic).length;

    return (
        <section className="space-y-3 rounded-xl border border-primary/40 bg-card p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h2 className="text-sm font-bold text-foreground">
                        Exportación preparada · {formatNumber(prepared.rows.length)} capturas
                    </h2>
                    <p className="text-xs text-muted-foreground">
                        {formatDateTime(prepared.createdAt)} · {formatNumber(privateCount)} privadas ·{' '}
                        {prepared.includeAllPhotos ? 'todas las fotos' : 'foto principal'} · {formatNumber(blocks)}{' '}
                        {blocks === 1 ? 'bloque' : 'bloques'} de fotos
                    </p>
                    {prepared.truncated && (
                        <p className="mt-1 text-xs text-amber-400">
                            El filtro supera {formatNumber(CAPTURE_DATA_MAX)} capturas: la lista está cortada. Acota los filtros para el resto.
                        </p>
                    )}
                </div>
                <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={onDownloadData} disabled={busy != null}>
                        <FileSpreadsheet size={16} className="mr-2" />
                        Datos (JSON + Excel)
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={onDiscard} disabled={busy != null} aria-label="Descartar exportación">
                        <X size={16} />
                    </Button>
                </div>
            </div>

            <p className="text-xs text-muted-foreground">
                Descomprime los ZIP de fotos en la misma carpeta que el de datos: cada captura indica en <code>bloque_fotos</code> dónde están sus fotos.
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                {Array.from({ length: blocks }, (_, index) => index + 1).map((block) => {
                    const running = busy?.kind === 'block' && busy.block === block;
                    const done = doneBlocks.has(block);
                    return (
                        <Button
                            key={block}
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => onDownloadBlock(block)}
                            disabled={busy != null || photoBlockRows(prepared.rows, block).every((row) => row.photos.length === 0)}
                            className={cn('justify-start', done && 'border-emerald-500/50 text-emerald-400')}
                        >
                            {running ? (
                                <RefreshCw size={14} className="mr-2 animate-spin" />
                            ) : done ? (
                                <Check size={14} className="mr-2" />
                            ) : (
                                <Images size={14} className="mr-2" />
                            )}
                            {running && busy?.kind === 'block'
                                ? `Fotos ${busy.done}/${busy.total}`
                                : `Fotos ${blockRange(block, prepared.rows.length)}`}
                        </Button>
                    );
                })}
            </div>
        </section>
    );
}
